import { useEffect, useState } from "react";

/* Visual-theme loader
   ---------------------------------------------------------------------------
   The dashboard ships several "looks" - Flat, Glass v1/v2/v3, Neumorphism,
   Neon - each as a self-contained CSS file under src/themes/. This module
   imports those files via Vite's `?inline` modifier (gets the CSS as a
   string at build time, no extra HTTP fetch) and injects the active one
   into a single `<style id="bv2-visual-theme">` tag in <head>.

   Why a single style tag, swapped textually? Because the CSS rules are
   thematically exclusive - only one set should be active at a time, and
   we want zero flash on switch. Replacing textContent on an existing tag
   is synchronous and atomic; the browser repaints once with the new rules.

   Independence from light/dark mode: the visual theme is the surface
   language (glassy / neon / etc); light vs dark is a colour mode that
   each theme implements internally via `html.dark .X` overrides. Both
   pieces of state are persisted independently. */

import flatCSS from "../themes/flat.css?inline";
import glassV1CSS from "../themes/glass-v1.css?inline";
import glassV2CSS from "../themes/glass-v2.css?inline";
import glassV3CSS from "../themes/glass-v3.css?inline";
import neumorphismCSS from "../themes/neumorphism.css?inline";
import neonCSS from "../themes/neon.css?inline";
import oceanCSS from "../themes/ocean.css?inline";
import sunsetCSS from "../themes/sunset.css?inline";
import forestCSS from "../themes/forest.css?inline";
import arcticCSS from "../themes/arctic.css?inline";
import lavenderCSS from "../themes/lavender.css?inline";
import roseGoldCSS from "../themes/rose-gold.css?inline";
import midnightCSS from "../themes/midnight.css?inline";
import copperCSS from "../themes/copper.css?inline";
import emeraldCSS from "../themes/emerald.css?inline";
import sakuraCSS from "../themes/sakura.css?inline";
import slateModernCSS from "../themes/slate-modern.css?inline";
import nordCSS from "../themes/nord.css?inline";
import glassExtremeCSS from "../themes/glass-extreme.css?inline";
import frostedGlassCSS from "../themes/frosted-glass.css?inline";
import glassPlusCSS from "../themes/glass-plus.css?inline";

// v0.4.0 catalog. Synced via waki-themes/scripts/sync-to-consumer.mjs.
import auroraTwilightCSS from "../themes/aurora-twilight.css?inline";
import auroraSunriseCSS from "../themes/aurora-sunrise.css?inline";
import auroraOceanCSS from "../themes/aurora-ocean.css?inline";
import auroraForestCSS from "../themes/aurora-forest.css?inline";
import cleanLightCSS from "../themes/clean-light.css?inline";
import cleanDimCSS from "../themes/clean-dim.css?inline";
import cleanWarmCSS from "../themes/clean-warm.css?inline";
import cleanCoolCSS from "../themes/clean-cool.css?inline";
import editorialAcademicCSS from "../themes/editorial-academic.css?inline";
import editorialNoirCSS from "../themes/editorial-noir.css?inline";
import editorialWarmCSS from "../themes/editorial-warm.css?inline";
import editorialTechnicalCSS from "../themes/editorial-technical.css?inline";
import neonCyanCSS from "../themes/neon-cyan.css?inline";
import neonPinkCSS from "../themes/neon-pink.css?inline";
import neonLimeCSS from "../themes/neon-lime.css?inline";
import neonPlasmaCSS from "../themes/neon-plasma.css?inline";

export type VisualTheme =
  | "flat"
  | "glass-v1"
  | "glass-v2"
  | "glass-v3"
  | "glass-extreme"
  | "frosted-glass"
  | "glass-plus"
  | "aurora-twilight"
  | "aurora-sunrise"
  | "aurora-ocean"
  | "aurora-forest"
  | "clean-light"
  | "clean-dim"
  | "clean-warm"
  | "clean-cool"
  | "editorial-academic"
  | "editorial-noir"
  | "editorial-warm"
  | "editorial-technical"
  | "neon-cyan"
  | "neon-pink"
  | "neon-lime"
  | "neon-plasma"
  | "neumorphism"
  | "neon"
  | "ocean"
  | "sunset"
  | "forest"
  | "arctic"
  | "lavender"
  | "rose-gold"
  | "midnight"
  | "copper"
  | "emerald"
  | "sakura"
  | "slate-modern"
  | "nord";

export interface ThemeMeta {
  id: VisualTheme;
  name: string;
  description: string;
}

export const AVAILABLE_THEMES: ThemeMeta[] = [
  // v0.4.0 curated catalog. Glass family first (Glass Plus is the
  // universal default), then Aurora / Clean / Editorial / Neon.
  { id: "glass-plus", name: "Glass Plus", description: "Richer accents, violet wash. Universal default." },
  { id: "glass-v2", name: "Glass", description: "Frosted glass + drift, calm baseline" },
  { id: "glass-v1", name: "Glass Lite", description: "Softer Glass, no glints" },
  { id: "frosted-glass", name: "Glass Frosted", description: "Heavy teal/cyan frost" },
  { id: "aurora-twilight", name: "Aurora Twilight", description: "Violet, cyan, pink aurora blobs" },
  { id: "aurora-sunrise", name: "Aurora Sunrise", description: "Gold, peach, pink" },
  { id: "aurora-ocean", name: "Aurora Ocean", description: "Teal, sky, blue" },
  { id: "aurora-forest", name: "Aurora Forest", description: "Emerald, lime, teal" },
  { id: "clean-light", name: "Clean Light", description: "Pure white canvas, near-black ink" },
  { id: "clean-dim", name: "Clean Dim", description: "Slate canvas with blue accent" },
  { id: "clean-warm", name: "Clean Warm", description: "Stone canvas, amber accent" },
  { id: "clean-cool", name: "Clean Cool", description: "Sky canvas, cyan accent" },
  { id: "editorial-academic", name: "Editorial Academic", description: "Stone-paper canvas, royal-blue ink" },
  { id: "editorial-noir", name: "Editorial Noir", description: "High-contrast black-on-stone with crimson accent" },
  { id: "editorial-warm", name: "Editorial Warm", description: "Cream paper, sienna ink" },
  { id: "editorial-technical", name: "Editorial Technical", description: "Slate canvas with engineering-doc restraint" },
  { id: "neon-cyan", name: "Neon Cyan", description: "Cyber-cyan accent on charcoal" },
  { id: "neon-pink", name: "Neon Pink", description: "Hot-pink accent on charcoal" },
  { id: "neon-lime", name: "Neon Lime", description: "Lime-green accent on charcoal" },
  { id: "neon-plasma", name: "Neon Plasma", description: "Plasma-orange + magenta two-tone" },

  // Legacy themes pre-v0.4.0. Kept selectable so saved localStorage
  // values still resolve cleanly; the DROPPED_REMAP forwards-migrates
  // any user that lands on a dropped id to glass-plus on read.
  { id: "flat", name: "Flat (legacy)", description: "Pre-v0.4.0; moving to clean-light" },
  { id: "glass-v3", name: "Glass v3 (legacy)", description: "Pre-v0.4.0 Aurora; renamed to aurora-twilight" },
  { id: "glass-extreme", name: "Glass Extreme (legacy)", description: "Pre-v0.4.0 ultra-translucent" },
];

const STYLE_TAG_ID = "bv2-visual-theme";
const STORAGE_KEY = "bv2:visual-theme";

// Universal default per waki-themes v0.4.0. Existing users on a
// dropped id (flat / neon / midnight / etc.) get auto-migrated to
// glass-plus by the readSaved remap below.
const DEFAULT: VisualTheme = "glass-plus";

// v0.4.0 dropped 16 themes from waki-themes. The local CSS files
// stay for backward compat but new selections default to glass-plus
// and stored selections that match a dropped id are migrated forward
// on read.
const DROPPED_REMAP: Partial<Record<string, VisualTheme>> = {
  flat: "glass-plus",
  "slate-modern": "glass-plus",
  nord: "glass-plus",
  arctic: "glass-plus",
  neumorphism: "glass-plus",
  lavender: "glass-plus",
  emerald: "glass-plus",
  "rose-gold": "glass-plus",
  copper: "glass-plus",
  neon: "glass-plus",
  midnight: "glass-plus",
  ocean: "glass-plus",
  sunset: "glass-plus",
  forest: "glass-plus",
  sakura: "glass-plus",
  "glass-extreme": "glass-plus",
};

const cssMap: Record<VisualTheme, string> = {
  "glass-plus": glassPlusCSS,
  flat: flatCSS,
  "glass-v1": glassV1CSS,
  "glass-v2": glassV2CSS,
  "glass-v3": glassV3CSS,
  "glass-extreme": glassExtremeCSS,
  "frosted-glass": frostedGlassCSS,
  "aurora-twilight": auroraTwilightCSS,
  "aurora-sunrise": auroraSunriseCSS,
  "aurora-ocean": auroraOceanCSS,
  "aurora-forest": auroraForestCSS,
  "clean-light": cleanLightCSS,
  "clean-dim": cleanDimCSS,
  "clean-warm": cleanWarmCSS,
  "clean-cool": cleanCoolCSS,
  "editorial-academic": editorialAcademicCSS,
  "editorial-noir": editorialNoirCSS,
  "editorial-warm": editorialWarmCSS,
  "editorial-technical": editorialTechnicalCSS,
  "neon-cyan": neonCyanCSS,
  "neon-pink": neonPinkCSS,
  "neon-lime": neonLimeCSS,
  "neon-plasma": neonPlasmaCSS,
  neumorphism: neumorphismCSS,
  neon: neonCSS,
  ocean: oceanCSS,
  sunset: sunsetCSS,
  forest: forestCSS,
  arctic: arcticCSS,
  lavender: lavenderCSS,
  "rose-gold": roseGoldCSS,
  midnight: midnightCSS,
  copper: copperCSS,
  emerald: emeraldCSS,
  sakura: sakuraCSS,
  "slate-modern": slateModernCSS,
  nord: nordCSS,
};

function readSaved(): VisualTheme | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const remapped = (DROPPED_REMAP as Record<string, VisualTheme>)[v];
      if (remapped) {
        try {
          localStorage.setItem(STORAGE_KEY, remapped);
        } catch {
          /* ignore */
        }
        return remapped;
      }
      if (Object.prototype.hasOwnProperty.call(cssMap, v)) {
        return v as VisualTheme;
      }
    }
  } catch {
    /* private mode etc */
  }
  return null;
}

let current: VisualTheme = readSaved() ?? DEFAULT;
const listeners = new Set<(t: VisualTheme) => void>();

function applyToDocument(theme: VisualTheme) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.setAttribute("data-bv2-theme", theme);
  tag.textContent = cssMap[theme] || cssMap[DEFAULT];
  document.documentElement.setAttribute("data-bv2-theme", theme);
}

applyToDocument(current);

export function getTheme(): VisualTheme {
  return current;
}

export function setTheme(theme: VisualTheme) {
  if (!cssMap[theme]) return;
  current = theme;
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
  applyToDocument(theme);
  for (const l of listeners) l(theme);
}

/** Subscribe to theme changes from React. */
export function useVisualTheme(): {
  theme: VisualTheme;
  setTheme: (t: VisualTheme) => void;
} {
  const [theme, setLocal] = useState<VisualTheme>(current);
  useEffect(() => {
    const fn = (t: VisualTheme) => setLocal(t);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return { theme, setTheme };
}
